import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import { useRecentVisits } from './data';
import {
  HomeCard,
  HomeEmptyState,
  HomeErrorState,
  HomeSkeletonRows,
  KindBadge,
} from './HomeCard';
import { formatTimeAgo, visitBadge, visitContext } from './viewModel';
import { FONT_DATA } from '../../theme/themes';

// "Pick up where you left off" — the user's most recent pages, from the home
// plugin's visits API (enabled in app-config: api:home/visits).
export const RecentVisitsCard = () => {
  const { visits, loading, error, retry } = useRecentVisits();

  return (
    <HomeCard title="Pick up where you left off" eyebrow="Recent">
      {loading && <HomeSkeletonRows rows={4} />}
      {!loading && error && (
        <HomeErrorState
          title="Couldn't load your recent activity"
          description="The visits service didn't respond. Try again."
          onRetry={retry}
        />
      )}
      {!loading && !error && visits?.length === 0 && (
        <HomeEmptyState
          icon={<HistoryRoundedIcon sx={{ fontSize: 22, opacity: 0.6 }} />}
          title="Nothing here yet"
          action={
            <Button size="small" component={RouterLink} to="/catalog">
              Explore the catalog
            </Button>
          }
        >
          Pages you visit appear here so you can jump straight back in.
        </HomeEmptyState>
      )}
      {!loading &&
        !error &&
        visits?.map(visit => (
          <Box
            key={visit.id}
            component={RouterLink}
            to={visit.pathname}
            sx={theme => ({
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1,
              py: 1.1,
              mx: -1,
              borderRadius: '8px',
              textDecoration: 'none',
              color: 'inherit',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.09),
              },
              '&:hover .rowName': { color: 'primary.main' },
            })}
          >
            <KindBadge label={visitBadge(visit)} />
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'baseline',
                gap: 0.75,
              }}
            >
              <Typography
                className="rowName"
                sx={{
                  fontWeight: 600,
                  fontSize: 13.5,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {visit.name}
              </Typography>
              <Typography
                sx={{ color: 'text.secondary', fontSize: 12, flex: 'none' }}
              >
                {visitContext(visit.pathname)}
              </Typography>
            </Box>
            <Typography
              component="span"
              sx={{
                fontFamily: FONT_DATA,
                fontSize: 10.5,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                flex: 'none',
              }}
            >
              {formatTimeAgo(visit.timestamp, Date.now())}
            </Typography>
          </Box>
        ))}
    </HomeCard>
  );
};
